import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

/**
 * 令牌自动刷新Hook
 * 在令牌即将过期时自动刷新
 */
export const useTokenRefresh = () => {
  const { token } = useAuth();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }

    // 解析JWT令牌获取过期时间
    const parseTokenExpiration = (token: string): number | null => {
      try {
        // 检查是否是JWT格式（包含两个点）
        if (token.split('.').length !== 3) {
          // console.log('Token is not JWT format, skipping automatic refresh');
          return null;
        }
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null; // 转换为毫秒
      } catch (error) {
        console.log('Failed to parse token as JWT, skipping automatic refresh:', error);
        return null;
      }
    };

    const expirationTime = parseTokenExpiration(token);
    if (!expirationTime) {
      // 如果不是JWT格式或解析失败，跳过自动刷新
      return;
    }

    // 计算刷新时间（在过期前5分钟刷新）
    const refreshTime = expirationTime - Date.now() - 5 * 60 * 1000;

    if (refreshTime <= 0) {
      // 令牌即将过期，立即刷新
      authService.refreshToken().catch(error => {
        console.error('Token refresh failed:', error);
      });
      return;
    }

    // 设置定时器在适当时间刷新令牌
    refreshTimerRef.current = setTimeout(async () => {
      try {
        await authService.refreshToken();
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, refreshTime);

    // 清理函数
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);
}; 